import React, { useEffect, useState } from 'react';
import { Inputs, PackageType, BuildPayType, IndustryType, RevenueCadence, INDUSTRY_DEFAULTS } from '../utils/roiCalculations';
interface CalculatorInputsProps {
  inputs: Inputs;
  onChange: (newInputs: Inputs) => void;
  onReset?: () => void;
  onReview?: () => void;
  showActionButtons?: boolean;
}
export function CalculatorInputs({
  inputs,
  onChange,
  onReset,
  onReview,
  showActionButtons = true
}: CalculatorInputsProps) {
  const [expandedSections, setExpandedSections] = useState({
    companyInfo: true,
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModel: false
  });
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChange = (field: keyof Inputs, value: string | number) => {
    onChange({
      ...inputs,
      [field]: value
    });
  };
  // Handle numeric input changes - allow empty string, convert to number only when valid
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    // If empty, set to empty string to allow clearing
    if (value === '') {
      onChange({
        ...inputs,
        [field]: ''
      });
      return;
    }
    // Otherwise parse as float
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      onChange({
        ...inputs,
        [field]: numValue
      });
    }
  };
  // Update industry-specific defaults when industry changes
  const handleIndustryChange = (newIndustry: IndustryType) => {
    const defaults = INDUSTRY_DEFAULTS[newIndustry];
    onChange({
      ...inputs,
      industry: newIndustry,
      ticket: defaults.ticket,
      gpm: defaults.gpm,
      salesFirst90Total: defaults.salesFirst90Total,
      salesVetMonthly: defaults.salesVetMonthly,
      revenueCadence: defaults.revenueCadence,
      residualTicket: defaults.residualTicket
    });
  };
  return <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-lg max-w-4xl mx-auto" data-id="element-122" data-mp="16gaqryfxrfcb">
      <div className="space-y-4" data-id="element-123" data-mp="1ainxap2ibha5">
        {/* Company Information - Single Row (No Collapsible) */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-124" data-mp="wjfuznb1rt1q">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-125" data-mp="11hhcw4wqlmbm">
            {/* Company Name */}
            <div data-id="element-126" data-mp="1p7k1uc5cuc60">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-127" data-mp="1p71wmq0psnly">
                Company Name
              </label>
              <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} placeholder="Enter company name" className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-128" data-mp="1u5veeun1b0rx" />
            </div>

            {/* Industry */}
            <div data-id="element-129" data-mp="1hxf6fjninil">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" data-id="element-130" data-mp="1hduir2m9l0v">
                Industry
              </label>
              <select value={inputs.industry} onChange={e => handleIndustryChange(e.target.value as IndustryType)} className="w-full p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-131" data-mp="3snf9happcyrs">
                <option value="" data-id="element-132" data-mp="39pciiqdlgrrc">Select</option>
                <option data-id="element-133" data-mp="39qnsj7xmuvqw">Windows</option>
                <option data-id="element-134" data-mp="39rp3zdik9k95">Roofing</option>
                <option data-id="element-135" data-mp="3npdpmnkgcvf9">Solar (Sales)</option>
                <option data-id="element-136" data-mp="3nn11fso3potr">Solar (Vertical)</option>
                <option data-id="element-137" data-mp="3iprcsf8jdh6z">Solar (Community)</option>
                <option data-id="element-138" data-mp="3is59esiv71gn">Pest</option>
                <option data-id="element-139" data-mp="12yd9chds0dym">Fiber</option>
                <option data-id="element-140" data-mp="12xbojl6ykd3p">Deregulated Energy</option>
                <option data-id="element-141" data-mp="y01z45jkk1fv">Hardscaping</option>
                <option data-id="element-142" data-mp="xza52z5mebrp">Landscaping</option>
                <option data-id="element-143" data-mp="1t9zyi22hhxz6">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Retention Modeling - Collapsible */}
        <div data-id="element-144" data-mp="1y9l7747ghkp3">
          <div className="flex items-center justify-between p-3.5 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors border border-purple-200" onClick={() => toggleSection('retentionModel')} data-id="element-145" data-mp="1y9bdb5jaite4">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-146" data-mp="2c9m8ulvjow5m">
              Retention Modeling
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-147" data-mp="2h6mbsnx3lisi">
              {expandedSections.retentionModel ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.retentionModel && <div className="mt-3 bg-purple-50 p-3 rounded border-2 border-purple-200" data-id="element-148" data-mp="2qx7bkeoictd0">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-149" data-mp="2vy4pgf2hj18p">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-150" data-mp="2zxx3h7andzlg">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-151" data-mp="ob3atj38dbzc">
                    Hired to Launched
                  </label>
                  <div className="w-56 relative" data-id="element-152" data-mp="k8oq0tg0425f">
                    <input type="number" min="0" max="100" step="1" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-153" data-mp="fa55mxh22yf6" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-154" data-mp="t94ua54kjdbc">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-155" data-mp="1ly22fff2ftqd">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-156" data-mp="3g6fkfoud5ryb">
                    Launched to Day 30
                  </label>
                  <div className="w-56 relative" data-id="element-157" data-mp="3kado8cumvqxp">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-158" data-mp="36ftbuel2e1p4" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-159" data-mp="2shwg1kefpzbi">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-160" data-mp="qoypjv84ritq">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-161" data-mp="10ughmwed3sgu">
                    Day 30 to Day 90
                  </label>
                  <div className="w-56 relative" data-id="element-162" data-mp="19lyxt7bb689x">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-163" data-mp="19ms64cpx15u6" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-164" data-mp="1ok107e6lsnpl">
                      %
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-165" data-mp="3qcz01tyssgk1">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-166" data-mp="ipn634o7jf20">
                    Day 90 to 1 Year
                  </label>
                  <div className="w-56 relative" data-id="element-167" data-mp="sfg75bqq4bs0">
                    <input type="number" min="0" max="100" step="1" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-168" data-mp="sgrm87pmd5yf" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-169" data-mp="dopfc3p4tcow">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Sales Performance - Collapsible */}
        <div data-id="element-170" data-mp="1ubcjqrzh4vzr">
          <div className="flex items-center justify-between p-3.5 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors border border-amber-200" onClick={() => toggleSection('salesPerformance')} data-id="element-171" data-mp="1uasz33ifvti1">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-172" data-mp="1lgoc4v6ff27s">
              Sales Performance
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-173" data-mp="1bkl3r7kze7ce">
              {expandedSections.salesPerformance ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.salesPerformance && <div className="mt-3 bg-amber-50 p-3 rounded border-2 border-amber-200" data-id="element-174" data-mp="12k6g41kjog5q">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-175" data-mp="30zj1uk7l3aab">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-176" data-mp="30yq3a6is3r39">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-177" data-mp="2wz8xfbzfwrxk">
                    Sales per 90 Day Rep
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-178" data-mp="1ot9076hrfit0" />
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-179" data-mp="1jue8ff1tejwr">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-180" data-mp="1jvpe5ketm1qi">
                    Monthly Sales per Veteran
                  </label>
                  <input type="number" min="0" step="0.5" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-181" data-mp="rca8dp7rm9dx" />
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-182" data-mp="2t3afx5nedv6n">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-183" data-mp="3gs3df6jo4b3d">
                    Pullthrough / Realization
                  </label>
                  <div className="w-56 relative" data-id="element-184" data-mp="37rnfsdplvy6f">
                    <input type="number" min="0" max="100" step="1" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-185" data-mp="37qvhgv4mjmcg" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-186" data-mp="1ycsz5vw1rtj">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Revenue & Margins - Collapsible */}
        <div data-id="element-187" data-mp="3fb65f2tcupnz">
          <div className="flex items-center justify-between p-3.5 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors border border-green-200" onClick={() => toggleSection('revenueMargins')} data-id="element-188" data-mp="3famysi95nnp9">
            <h3 className="text-sm md:text-base font-semibold text-navy" data-id="element-189" data-mp="36caw3hv0kzgg">
              Revenue & Margins
            </h3>
            <span className="text-navy font-bold text-base" data-id="element-190" data-mp="18idotcb3nnm7">
              {expandedSections.revenueMargins ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.revenueMargins && <div className="mt-3 bg-green-50 p-3 rounded border-2 border-green-200" data-id="element-191" data-mp="2rfuohjbmbvr5">
              <div className="space-y-2.5 flex flex-col items-center" data-id="element-192" data-mp="2rdhx6fjz091f">
                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-193" data-mp="2w72yaggv1j4u">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-194" data-mp="3073bjxs83oab">
                    Revenue Cadence
                  </label>
                  <select value={inputs.revenueCadence} onChange={e => handleChange('revenueCadence', e.target.value as RevenueCadence)} className="w-56 p-2 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-195" data-mp="23k8i3vr2t5x6">
                    <option value="one-time" data-id="element-196" data-mp="3sv8y8q2u7ncs">One-Time Sale</option>
                    <option value="monthly" data-id="element-197" data-mp="3rev6lymj0i9b">Monthly Residual</option>
                    <option value="quarterly" data-id="element-198" data-mp="3mgjwrnw8e7r3">Quarterly Residual</option>
                  </select>
                </div>

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-199" data-mp="4bgnrs359ble">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-200" data-mp="9d80r9fzkub">
                    {inputs.revenueCadence === 'one-time' ? 'Average Ticket (Revenue per Sale)' : 'Initial Sale Ticket'}
                  </label>
                  <div className="w-56 relative" data-id="element-201" data-mp="97xkhaoesya3">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-202" data-mp="nz7l99xobbf3">
                      $
                    </span>
                    <input type="number" min="0" step="500" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-203" data-mp="wzm9ah20v4ma" />
                  </div>
                </div>

                {inputs.revenueCadence !== 'one-time' && <>
                    <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-204" data-mp="svbldm4fgg1i">
                      <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-205" data-mp="xmjwqnpbibhb">
                        {inputs.revenueCadence === 'monthly' ? 'Monthly Residual Ticket' : 'Quarterly Residual Ticket'}
                      </label>
                      <div className="w-56 relative" data-id="element-206" data-mp="9wzbfy5oz24v">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-207" data-mp="9yahy5qiunzo">
                          $
                        </span>
                        <input type="number" min="0" step="10" value={inputs.residualTicket || 0} onChange={e => handleNumericChange('residualTicket', e.target.value)} className="w-full p-2 pl-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-208" data-mp="2gm9pglci1ayi" />
                      </div>
                    </div>

                    {inputs.revenueCadence === 'monthly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-209" data-mp="2lmnq3ibrtimi">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-210" data-mp="1f25xh85wcbxy">
                          Monthly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-211" data-mp="3ltspjxfccazd">
                          <input type="number" min="0" max="100" step="1" value={inputs.monthlyAttrition || 2} onChange={e => handleNumericChange('monthlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-212" data-mp="3qrcfvvpbce7o" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-213" data-mp="2k3nneh9d1fw2">
                            %
                          </span>
                        </div>
                      </div>}

                    {inputs.revenueCadence === 'quarterly' && <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-214" data-mp="208mc29bi7adk">
                        <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-215" data-mp="2e12aewtgmk85">
                          Quarterly Attrition
                        </label>
                        <div className="w-56 relative" data-id="element-216" data-mp="1m9cx1cz1amb6">
                          <input type="number" min="0" max="100" step="1" value={inputs.quarterlyAttrition || 5} onChange={e => handleNumericChange('quarterlyAttrition', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-217" data-mp="1ma64kg5thfud" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-218" data-mp="3e6w8tjuyyixl">
                            %
                          </span>
                        </div>
                      </div>}
                  </>}

                <div className="flex items-center gap-3 w-full max-w-xl" data-id="element-219" data-mp="3szzqrsch2you">
                  <label className="text-sm font-semibold text-gray-700 w-44 text-right" data-id="element-220" data-mp="1r2enmld50aj">
                    Gross Profit Margin
                  </label>
                  <div className="w-56 relative" data-id="element-221" data-mp="3o6wx7k7p3p8n">
                    <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 pr-7 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors text-sm" data-id="element-222" data-mp="3o5lp5877vvbq" />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-sm" data-id="element-223" data-mp="22ba6u43q959p">
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {showActionButtons && onReset && <>
            <div className="border-t-2 border-gray-200 pt-4" data-id="element-224" data-mp="1jes832qgo53y">
              <button onClick={onReset} className="w-full px-4 py-2.5 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-bold text-sm transition-colors active:translate-y-[1px]" data-id="element-225" data-mp="1jglf4j4zkrch">
                Reset to Defaults
              </button>
            </div>

            <div className="text-sm text-gray-600 leading-relaxed border-t-2 border-gray-200 pt-4" data-id="element-226" data-mp="3svkuom6869nf">
              LMS note: access to a Learning Management System is included in
              all packages. In Edge it's more "DIY reference"; in
              Advantage/Summit it reinforces hands-on execution.
            </div>
          </>}

        {onReview && <div className="border-t-2 border-gray-200 pt-4" data-id="element-227" data-mp="pnws6s1qdwz">
            <button onClick={onReview} className="w-full py-3.5 md:py-4 rounded-lg font-extrabold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-228" data-mp="fflrz6oe83vo">
              Review Your Numbers →
            </button>
          </div>}
      </div>
    </div>;
}